import React, { useState } from 'react'
import { Feature } from '../../api/client'
import { ItemListEditorShell } from './ItemListEditorShell'
import LocationPickerModal, {
    LocationPickerInitial,
    LocationPickerResult,
} from './LocationPickerModal'
import LocationViewerModal, { LocationViewerValue } from './LocationViewerModal'

/**
 * Location list editor. Wraps `ItemListEditorShell` with the location-list
 * row shape and pops `LocationPickerModal` for both add and edit. Each row
 * is either a Places ref (`referenceValue` → user's private Places item) or
 * coords-only (`latitude`/`longitude`).
 *
 * On save, the shell hands the final list to the parent's `onChange`. The
 * caller (CheckinComposer.handleLocationListChange) does a row-level diff
 * against the existing rows: matched live rows are preserved verbatim
 * (AI-attached rows keep `manual_override = NULL`), unmatched live rows
 * become DELETE tombstones, and re-added values revive matching tombstones.
 */

export interface LocationRow {
    /** Stable row id for React keys; persisted across re-render but not sent
     *  to the backend (composer constructs fresh CheckinFeature rows). */
    id: string
    referenceValue?: number
    latitude?: number
    longitude?: number
    /** UX-only fallback display name carried by rows the user just picked
     *  through the modal. The parent's `collectionItems` snapshot lags the
     *  brand-new Places item created by /find_or_create, so without this we
     *  render "Place #N" until the next reload. Stripped at save time —
     *  composer only reads referenceValue/latitude/longitude. */
    nameHint?: string
}

/**
 * Set-enforcement duplicate test (ENFORCE_AS_SET_PLAN Stage 2). Location
 * identity matches `_row_value_signature`'s XOR shape: a Places-ref row
 * collides on `referenceValue`; a coords-only row collides on exact
 * (lat, lng). A ref row and a coords row never collide, even when they point
 * at the same physical spot — same as the backend signature.
 */
export const isDuplicateLocation = (
    candidate: LocationRow,
    others: LocationRow[],
): boolean => {
    if (candidate.referenceValue != null) {
        return others.some((o) => o.referenceValue === candidate.referenceValue)
    }
    if (candidate.latitude != null && candidate.longitude != null) {
        return others.some(
            (o) => o.latitude === candidate.latitude && o.longitude === candidate.longitude,
        )
    }
    return false
}

interface LocationListEditorProps {
    feature: Feature
    values: LocationRow[]
    /** Optional: lookup of collection items, keyed by stringified id. Used
     *  to render Places-ref rows by name when available. Coords rows are
     *  rendered without it. */
    collectionItems?: Record<string, any>
    /** Forwarded to `LocationPickerModal` so freshly-created Places land in
     *  the page-level cache. Without this, a save+close+reopen of the list
     *  renders the new row as "Place #N". */
    onCollectionItemReady?: (item: { id: number; data: Record<string, any> }) => void
    onChange: (values: LocationRow[]) => void
    onClose: () => void
}

const LocationListEditor: React.FC<LocationListEditorProps> = ({
    feature,
    values,
    collectionItems,
    onCollectionItemReady,
    onChange,
    onClose,
}) => {
    // ENFORCE_AS_SET_PLAN Stage 2: reject re-adding the same location when the
    // feature disallows repetitions.
    const enforceSet = feature.config?.enforce_as_set === true

    const [pickerOpen, setPickerOpen] = useState(false)
    const [pickerInitial, setPickerInitial] = useState<LocationPickerInitial | null>(null)
    // For edits we route the picker's confirm through this ref's callback;
    // adds use the shell's `addItem` via `renderAddTrigger`.
    const pendingResolverRef = React.useRef<((r: LocationPickerResult) => void) | null>(null)

    // Row-tap opens the read-only viewer first; viewer's Edit button hands
    // off to the picker. `pendingEditRef` carries the (initial, replace)
    // pair across the viewer→picker swap so we don't lose the row identity.
    const [viewerOpen, setViewerOpen] = useState(false)
    const [viewerValue, setViewerValue] = useState<LocationViewerValue | null>(null)
    const pendingEditRef = React.useRef<{
        initial: LocationPickerInitial | null
        replace: (newRow: LocationRow) => void
    } | null>(null)

    const openPickerFor = (
        initial: LocationPickerInitial | null,
        onResult: (r: LocationPickerResult) => void,
    ) => {
        setPickerInitial(initial)
        pendingResolverRef.current = onResult
        setPickerOpen(true)
    }

    const handlePickerConfirm = (result: LocationPickerResult) => {
        const fn = pendingResolverRef.current
        pendingResolverRef.current = null
        setPickerOpen(false)
        if (fn) fn(result)
    }

    const resultToRow = (result: LocationPickerResult): LocationRow => {
        if (result.kind === 'place') {
            return {
                id: `loc-${Date.now()}`,
                referenceValue: result.collectionItemId,
                nameHint: result.name,
            }
        }
        return { id: `loc-${Date.now()}`, latitude: result.latitude, longitude: result.longitude }
    }

    const rowToInitial = (row: LocationRow): LocationPickerInitial | null => {
        if (row.referenceValue != null) {
            const item = collectionItems?.[String(row.referenceValue)]
            return {
                kind: 'place',
                collectionItemId: row.referenceValue,
                lat: item?.data?.lat,
                lng: item?.data?.lng,
                name: item?.data?.name,
                googlePlaceId: item?.data?.google_place_id,
            }
        }
        if (row.latitude != null && row.longitude != null) {
            return { kind: 'coords', latitude: row.latitude, longitude: row.longitude }
        }
        return null
    }

    const rowToViewerValue = (row: LocationRow): LocationViewerValue | null => {
        if (row.referenceValue != null) {
            const item = collectionItems?.[String(row.referenceValue)]
            const lat = item?.data?.lat
            const lng = item?.data?.lng
            if (lat == null || lng == null) return null
            return {
                lat,
                lng,
                name: item?.data?.name || row.nameHint,
                address: item?.data?.address,
                googlePlaceId: item?.data?.google_place_id,
            }
        }
        if (row.latitude != null && row.longitude != null) {
            return { lat: row.latitude, lng: row.longitude }
        }
        return null
    }

    const openViewerForEdit = (row: LocationRow, replace: (newRow: LocationRow) => void) => {
        const value = rowToViewerValue(row)
        if (!value) {
            // Falls back to opening the picker directly when we can't
            // construct a viewer payload (e.g., place ref whose collection
            // item hasn't loaded). Keeps the row interactive in that edge.
            openPickerFor(rowToInitial(row), (result) => replace(resultToRow(result)))
            return
        }
        pendingEditRef.current = { initial: rowToInitial(row), replace }
        setViewerValue(value)
        setViewerOpen(true)
    }

    const handleViewerEdit = () => {
        const pending = pendingEditRef.current
        setViewerOpen(false)
        if (!pending) return
        openPickerFor(pending.initial, (result) => pending.replace(resultToRow(result)))
    }

    return (
        <>
            <ItemListEditorShell<LocationRow>
                feature={feature}
                initialItems={values}
                renderRow={(row) => <LocationRowDisplay row={row} collectionItems={collectionItems} />}
                renderAddTrigger={(onAdd) => (
                    <button
                        onClick={() =>
                            openPickerFor(null, (result) => onAdd(resultToRow(result)))
                        }
                        className="w-full px-3 py-2 text-sm border border-dashed border-gray-300 rounded text-gray-600 hover:border-amber-300 hover:text-amber-900"
                    >
                        + Add location
                    </button>
                )}
                onRowClick={(row, _index, replace) => {
                    openViewerForEdit(row, replace)
                }}
                onChange={onChange}
                onClose={onClose}
                emptyText="No locations yet"
                isDuplicate={enforceSet ? isDuplicateLocation : undefined}
                duplicateMessage="Location already added"
            />

            <LocationViewerModal
                open={viewerOpen}
                value={viewerValue}
                // Reference rows view-only: editing a saved Place from the
                // composer is confusing — the picker would either no-op (Google
                // dedup) or fork into a new private place. The × button on
                // the row already covers "remove and re-add". Coords-only rows
                // still get Edit since there's no underlying Place to mutate.
                onEdit={
                    pendingEditRef.current?.initial?.kind === 'coords'
                        ? handleViewerEdit
                        : undefined
                }
                onClose={() => {
                    pendingEditRef.current = null
                    setViewerOpen(false)
                }}
            />

            <LocationPickerModal
                open={pickerOpen}
                initial={pickerInitial}
                onConfirm={handlePickerConfirm}
                onCollectionItemReady={onCollectionItemReady}
                onClose={() => {
                    pendingResolverRef.current = null
                    setPickerOpen(false)
                }}
            />
        </>
    )
}

const LocationRowDisplay: React.FC<{
    row: LocationRow
    collectionItems?: Record<string, any>
}> = ({ row, collectionItems }) => {
    if (row.referenceValue != null) {
        const item = collectionItems?.[String(row.referenceValue)]
        const name = item?.data?.name || row.nameHint
        return <span>{name || `Place #${row.referenceValue}`}</span>
    }
    if (row.latitude != null && row.longitude != null) {
        return <span>{row.latitude.toFixed(2)}°, {row.longitude.toFixed(2)}°</span>
    }
    return <span className="text-gray-400">(empty)</span>
}

export default LocationListEditor
